import { createContext, useContext, useEffect, useState } from 'react';
import useProvincesActions from '../hooks/APIHooks/users/useProvincesActions';
import { Province, ProvinceReqBody } from '../types/province';

// datatype of the context declared here
interface IProvinceContext {
  targetedProvince: Province | null;
  provinces: Array<Province>;
  selectedProvinces: Array<ProvinceReqBody>;
  provinceAddedCountries: Array<string>;
  filterCountry: string;
  searchText: string;
  showDeleteModal: boolean;
  showEditModal: boolean;
  setTargetedProvince: React.Dispatch<React.SetStateAction<Province | null>>;
  setSelectedProvinces: React.Dispatch<
    React.SetStateAction<Array<ProvinceReqBody>>
  >;
  setFilterCountry: React.Dispatch<React.SetStateAction<string>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteProvinceModalHandler: (province: Province) => void;
  showEditProvinceModalHandler: (province: Province) => void;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

// creating the context here
const ProvinceContext = createContext<IProvinceContext | null>(null);

const ProvinceProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [targetedProvince, setTargetedProvince] = useState<Province | null>(
    null
  );
  const [provinces, setProvinces] = useState<Array<Province>>([]);
  const [selectedProvinces, setSelectedProvinces] = useState<
    Array<ProvinceReqBody>
  >([]);

  const [provinceAddedCountries, setProvinceAddedCountries] = useState<
    Array<string>
  >([]);
  const [filterCountry, setFilterCountry] = useState<string>('All');
  const [searchText, setSearchText] = useState<string>('');

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // integration of custom API hooks here
  const { getProvinceListHandler } = useProvincesActions('provinceList');

  console.log('PROVINCE LIST: ', getProvinceListHandler.data?.data);

  // extracting country names for filter province by country name options here here
  useEffect(() => {
    if (getProvinceListHandler.data?.is_success) {
      const countries = getProvinceListHandler.data.data.map(
        (province) => province.country_name
      );

      const countrySet = new Set(countries);

      const uniqueCountries = Array.from(countrySet);

      setProvinceAddedCountries(uniqueCountries.sort());
    }
  }, [getProvinceListHandler.data]);

  // filtering provinces based of filter country and search text here
  useEffect(() => {
    if (getProvinceListHandler.data?.is_success) {
      setProvinces(
        getProvinceListHandler.data.data
          .filter((province) =>
            filterCountry !== 'All'
              ? province.country_name.toLowerCase() ===
                filterCountry.toLowerCase()
              : province
          )
          .filter((province) =>
            searchText
              ? province.province_name
                  .toLowerCase()
                  .includes(searchText.toLowerCase())
              : province
          )
      );
    }
  }, [getProvinceListHandler.data, filterCountry, searchText]);

  // handler function to show delete province modal declared here
  const showDeleteProvinceModalHandler = (province: Province) => {
    setShowDeleteModal(true);
    setTargetedProvince(province);
  };

  // handler function to show edit province modal declared here
  const showEditProvinceModalHandler = (province: Province) => {
    setShowEditModal(true);
    setTargetedProvince(province);
  };

  // rendering the context provider here
  return (
    <ProvinceContext.Provider
      value={{
        targetedProvince,
        provinces,
        selectedProvinces,
        provinceAddedCountries,
        filterCountry,
        searchText,
        showDeleteModal,
        showEditModal,
        setTargetedProvince,
        setSelectedProvinces,
        setFilterCountry,
        setSearchText,
        setShowDeleteModal,
        setShowEditModal,
        showDeleteProvinceModalHandler,
        showEditProvinceModalHandler,
        isError: getProvinceListHandler.isError,
        isLoading: getProvinceListHandler.isLoading,
        isSuccess: getProvinceListHandler.isSuccess,
      }}
    >
      {children}
    </ProvinceContext.Provider>
  );
};

export default ProvinceProvider;

// custom hook to access province context declared here
export const useProvinceContext = () => {
  const context = useContext(ProvinceContext);

  if (!context) {
    throw new Error('useProvinceContext must be within ProvinceProvider!');
  }

  return context;
};
